import React, { useState, useEffect } from 'react';
import Header from '../../Header';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { MdCalendarToday, MdTrendingUp } from 'react-icons/md';
import { useUser } from '../../../contexts/userContext';
import api from '../../../services/api';
import ModalInvestimentos from '../../ModalInvestimentos';
import './style.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function PainelMKT() {
  const { user } = useUser();
  
  // Estados de autenticação
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  
  // Função para obter as datas do mês atual
  const getDatasMesAtual = () => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
    
    return {
      inicio: primeiroDia.toISOString().split('T')[0],
      fim: ultimoDia.toISOString().split('T')[0]
    };
  };

  const { inicio, fim } = getDatasMesAtual();
  
  const [dataInicial, setDataInicial] = useState(inicio);
  const [dataFinal, setDataFinal] = useState(fim);
  const [loading, setLoading] = useState(false);
  const [modalInvestimentosOpen, setModalInvestimentosOpen] = useState(false);
  const [filtrarTemperatura, setFiltrarTemperatura] = useState(false);

  // Dados das estatísticas
  const [stats, setStats] = useState({
    valorInvestido: 0.00,
    cardsEtapas: {
      cardsCriados: 0,
      criadosOrcados: 0,
      orcados: 0,
      orcadosVendidos: 0,
      vendidos: 0,
      perdidos: 0,
      valorOrcados: 0,
      valorVendidos: 0,
      valorPerdidos: 0
    }
  });

  // Função para autenticação
  const handleLogin = (e) => {
    e.preventDefault();
    const senhaCorreta = 'mkt123';
    
    if (password === senhaCorreta) {
      setIsAuthenticated(true);
      setAuthError('');
      setPassword('');
    } else {
      setAuthError('Senha incorreta. Tente novamente.');
      setPassword('');
    }
  };

  // useEffect para carregar dados do mês atual automaticamente
  useEffect(() => {
    if (user?.empresa_id && dataInicial && dataFinal && isAuthenticated) {
      buscarDados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.empresa_id, isAuthenticated, filtrarTemperatura]); // Executa quando o user está disponível e autenticado

  // Função para buscar dados do backend
  const buscarDados = async () => {
    if (!dataInicial || !dataFinal || !user?.empresa_id) return;
    
    setLoading(true);
    try {
      console.log('Enviando filtrarTemperatura:', filtrarTemperatura);
      
      // Buscar estatísticas dos cards
      const responseStats = await api.get('/marketing/estatisticas-painel', {
        params: {
          dataInicial,
          dataFinal,
          empresaId: user.empresa_id,
          filtrarTemperatura: filtrarTemperatura ? 'true' : 'false'
        }
      });

      // Buscar total investido
      const responseInvestido = await api.get('/marketing/total-investido', {
        params: {
          dataInicial,
          dataFinal,
          empresaId: user.empresa_id,
          filtrarTemperatura: filtrarTemperatura ? 'true' : 'false'
        }
      });

      const dados = responseStats.data;
      const totalInvestido = responseInvestido.data?.total_investido || 0;
      
      console.log('Dados recebidos:', dados);
      
      setStats({
        valorInvestido: parseFloat(totalInvestido),
        cardsEtapas: {
          cardsCriados: parseInt(dados.total_cards_criados) || 0,
          criadosOrcados: parseFloat(dados.percentual_criados_orcados) || 0,
          orcados: parseInt(dados.cards_orcados) || 0,
          orcadosVendidos: parseFloat(dados.percentual_orcados_vendidos) || 0,
          vendidos: parseInt(dados.cards_vendidos) || 0,
          perdidos: parseInt(dados.cards_perdidos) || 0,
          valorOrcados: parseFloat(dados.valor_total_orcados) || 0,
          valorVendidos: parseFloat(dados.valor_total_vendidos) || 0,
          valorPerdidos: parseFloat(dados.valor_total_perdidos) || 0
        }
      });
    } catch (error) {
      console.error('Erro ao buscar dados do painel MKT:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para aplicar filtro
  const aplicarFiltro = () => {
    buscarDados();
  };

  // Gráfico 1: Funil de Conversão (Quantidade de Cards por Status)
  const chartData1 = {
    labels: ['Criados', 'Orçados', 'Vendidos', 'Perdidos'],
    datasets: [
      {
        label: 'Quantidade de Cards',
        data: [
          stats.cardsEtapas.cardsCriados,
          stats.cardsEtapas.orcados,
          stats.cardsEtapas.vendidos,
          stats.cardsEtapas.perdidos
        ],
        backgroundColor: [
          '#3b82f6', // Azul para criados
          '#f59e0b', // Amarelo para orçados
          '#10b981', // Verde para vendidos
          '#ef4444'  // Vermelho para perdidos
        ],
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#ffffff'
      }
    ]
  };

  // Gráfico 2: Distribuição de Valores (em Reais)
  const chartData2 = {
    labels: ['Valor Orçado', 'Valor Vendido', 'Valor Perdido'],
    datasets: [
      {
        label: 'Valor (R$)',
        data: [
          stats.cardsEtapas.valorOrcados,
          stats.cardsEtapas.valorVendidos,
          stats.cardsEtapas.valorPerdidos
        ],
        backgroundColor: [
          '#8b5cf6', // Roxo para orçados
          '#06b6d4', // Ciano para vendidos
          '#f97316'  // Laranja para perdidos
        ],
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#ffffff'
      }
    ]
  };

  // Opções para o gráfico de quantidade de cards
  const chartOptions1 = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Funil de Conversão - Quantidade de Cards',
        font: {
          size: 16,
          weight: 'bold'
        },
        color: '#1f2937'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.label + ': ' + context.parsed.y + ' cards';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: '#e5e7eb',
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
          },
          stepSize: 1
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
          },
        },
      },
    },
  };

  // Opções para o gráfico de valores
  const chartOptions2 = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Distribuição de Valores - Análise Financeira',
        font: {
          size: 16,
          weight: 'bold'
        },
        color: '#1f2937'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return context.label + ': R$ ' + value.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: '#e5e7eb',
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
          },
          callback: function(value) {
            return 'R$ ' + value.toLocaleString('pt-BR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            });
          }
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6b7280',
          font: {
            size: 12,
          },
        },
      },
    },
  };

  // Se não estiver autenticado, mostrar tela de login
  if (!isAuthenticated) {
    return (
      <div className="painel-mkt-container">
        <Header />
        
        <div className="auth-container">
          <div className="auth-card">
            <div className="auth-header">
              <h2>🔒 Acesso Restrito</h2>
              <p>Digite a senha para acessar o Painel MKT</p>
            </div>
            
            <form onSubmit={handleLogin} className="auth-form">
              <div className="auth-input-group">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite a senha"
                  className="auth-input"
                  autoFocus
                />
              </div>
              
              {authError && (
                <div className="auth-error">
                  {authError}
                </div>
              )}
              
              <button type="submit" className="auth-button">
                Acessar Painel
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="painel-mkt-container">
      <Header />
      
      <div className="painel-mkt-content">
        {/* Cabeçalho */}
        <div className="painel-mkt-header">
          <div className="painel-mkt-title-section">
            <h1 className="painel-mkt-title">📊 Painel MKT</h1>
            <p className="painel-mkt-subtitle">Acompanhamento de performance do MKT</p>
          </div>
          
          <div className="painel-mkt-filter">
            <div className="date-inputs">
              <input
                type="date"
                value={dataInicial}
                onChange={(e) => setDataInicial(e.target.value)}
                className="date-input"
                placeholder="Data inicial"
              />
              <input
                type="date"
                value={dataFinal}
                onChange={(e) => setDataFinal(e.target.value)}
                className="date-input"
                placeholder="Data final"
              />
              <button 
                className="filter-button" 
                onClick={aplicarFiltro}
                disabled={!dataInicial || !dataFinal || loading}
              >
                <MdCalendarToday className="filter-icon" />
                {loading ? 'Carregando...' : 'Filtrar período'}
              </button>
              <button 
                className={`temperatura-filter-button ${filtrarTemperatura ? 'active' : ''}`}
                onClick={() => setFiltrarTemperatura(!filtrarTemperatura)}
                disabled={loading}
                title={filtrarTemperatura ? 'Mostrando apenas cards com temperatura > 0 (1,2,3,4,5)' : 'Mostrando todos os cards (incluindo 0, NULL, vazio)'}
              >
                🌡️ {filtrarTemperatura ? 'Com Temperatura' : 'Todos'}
              </button>
            </div>
          </div>
        </div>

        {/* Valor Investido */}
        <div className="valor-investido-section">
          <div className="valor-investido-content">
            <span className="valor-investido-label">Valor Investido</span>
            <span className="valor-investido-value">R$ {stats.valorInvestido.toFixed(2).replace('.', ',')}</span>
          </div>
          <button 
            className="btn-investimentos" 
            onClick={() => setModalInvestimentosOpen(true)}
            title="Gerenciar Investimentos"
          >
            <MdTrendingUp /> Investimentos
          </button>
        </div>

        {/* Cards de Estatísticas */}
        <div className="stats-cards">
          <div className="stat-card">
            <div className="stat-number">{stats.cardsEtapas.cardsCriados}</div>
            <div className="stat-label">Cards Criados</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-number">{stats.cardsEtapas.criadosOrcados}%</div>
            <div className="stat-label">Criados - Orçados</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-number">{stats.cardsEtapas.orcados}</div>
            <div className="stat-label">Orçados</div>
            <div className="stat-subtitle">R$ {stats.cardsEtapas.valorOrcados.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-number">{stats.cardsEtapas.orcadosVendidos}%</div>
            <div className="stat-label">Orçados - Vendidos</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-number">{stats.cardsEtapas.vendidos}</div>
            <div className="stat-label">Vendidos</div>
            <div className="stat-subtitle">R$ {stats.cardsEtapas.valorVendidos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
          
          <div className="stat-card">
            <div className="stat-number">{stats.cardsEtapas.perdidos}</div>
            <div className="stat-label">Perdidos</div>
            <div className="stat-subtitle">R$ {stats.cardsEtapas.valorPerdidos.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          </div>
        </div>

        {/* Gráficos */}
        <div className="charts-section">
          <div className="chart-container">
            <div className="chart-wrapper">
              <Bar data={chartData1} options={chartOptions1} />
            </div>
          </div>
          
          <div className="chart-container">
            <div className="chart-wrapper">
              <Bar data={chartData2} options={chartOptions2} />
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Investimentos */}
      <ModalInvestimentos 
        isOpen={modalInvestimentosOpen} 
        onClose={() => {
          setModalInvestimentosOpen(false);
          // Recarregar dados quando fechar o modal para atualizar o valor investido
          if (dataInicial && dataFinal) {
            buscarDados();
          }
        }} 
      />
    </div>
  );
}

export default PainelMKT; 